import React from 'react';
import { 
  Zap, 
  Brain, 
  Gauge,
  Circle,
  CircleDot
} from 'lucide-react';
import { ReasoningEffort, AVAILABLE_MODELS } from '../types/api';
import { cn } from '../lib/utils';

interface ModelSelectorProps {
  reasoningEffort: ReasoningEffort;
  onReasoningEffortChange: (effort: ReasoningEffort) => void;
  isLoading?: boolean;
}

const ModelSelector: React.FC<ModelSelectorProps> = ({
  reasoningEffort,
  onReasoningEffortChange,
  isLoading = false
}) => {
  const getReasoningIcon = (effort: ReasoningEffort) => {
    switch (effort) {
      case 'none':
        return <Zap className="h-4 w-4" />;
      default:
        return <Brain className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <label className="flex items-center space-x-2 text-sm font-medium">
        <Gauge className="h-4 w-4 text-muted-foreground" />
        <span>Reasoning</span>
      </label>
      
      {/* Model Cards */}
      <div className="grid grid-cols-4 gap-2">
        {AVAILABLE_MODELS.map((model) => {
          const isSelected = reasoningEffort === model.reasoningEffort;
          
          return (
            <button
              key={model.reasoningEffort}
              onClick={() => onReasoningEffortChange(model.reasoningEffort)}
              disabled={isLoading}
              className={cn(
                "relative p-3 rounded-lg border transition-all duration-200",
                "flex flex-col items-center justify-center text-center",
                isSelected 
                  ? "border-foreground bg-foreground text-background" 
                  : "border-border hover:border-foreground/50 bg-card",
                isLoading && "opacity-50 cursor-not-allowed"
              )}
            >
              <div className={cn(
                "p-1.5 rounded mb-1.5",
                isSelected ? "bg-background/20" : "bg-muted"
              )}>
                {getReasoningIcon(model.reasoningEffort)}
              </div>
              <span className="font-medium text-sm">{model.name}</span>
              <span className={cn(
                "text-[10px] mt-0.5",
                isSelected ? "text-background/70" : "text-muted-foreground"
              )}>
                {model.description}
              </span>
              
              {/* Selection indicator */}
              <div className="absolute top-1.5 right-1.5">
                {isSelected ? (
                  <CircleDot className="h-3 w-3" />
                ) : (
                  <Circle className="h-3 w-3 text-muted-foreground/30" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ModelSelector;
